import React, { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Mail, Loader2, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const Login = () => {
  const { user, loading, login } = useAuth();
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const loc = useLocation();

  if (loading) return null;
  if (user) {
    const next = loc.state?.from || "/admin";
    return <Navigate to={next} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    await login(email.trim(), pwd);
    setBusy(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-5 relative">
      <div className="absolute inset-0 -z-10">
        <div className="blob blob-cyan w-[420px] h-[420px] top-10 -left-20" />
        <div className="blob blob-violet w-[380px] h-[380px] bottom-20 -right-10" style={{ animationDelay: "-8s" }} />
      </div>
      <motion.form
        onSubmit={onSubmit}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md glass rounded-3xl p-8"
        data-testid="admin-login-form"
      >
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-400 to-violet-500 grid place-items-center">
            <ShieldCheck size={18} className="text-black" />
          </div>
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-cyan-300">Admin</span>
        </div>
        <h1 className="font-display text-3xl text-white font-medium mb-2">Sign in</h1>
        <p className="text-sm text-slate-400 mb-7">Manage your portfolio content.</p>

        <label className="block mb-4">
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-slate-500">Email</span>
          <div className="mt-2 flex items-center gap-2 bg-white/[0.03] border border-white/10 rounded-xl px-3 focus-within:border-cyan-400/40 transition-colors">
            <Mail size={15} className="text-slate-500" />
            <input
              data-testid="admin-login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="bg-transparent w-full py-3 text-white placeholder:text-slate-600 focus:outline-none"
            />
          </div>
        </label>

        <label className="block mb-6">
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-slate-500">Password</span>
          <div className="mt-2 flex items-center gap-2 bg-white/[0.03] border border-white/10 rounded-xl px-3 focus-within:border-cyan-400/40 transition-colors">
            <Lock size={15} className="text-slate-500" />
            <input
              data-testid="admin-login-password"
              type="password"
              required
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              placeholder="••••••••"
              className="bg-transparent w-full py-3 text-white placeholder:text-slate-600 focus:outline-none"
            />
          </div>
        </label>

        <button
          type="submit"
          disabled={busy}
          data-testid="admin-login-submit"
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-black font-medium text-sm hover:shadow-[0_0_40px_rgba(0,240,255,0.4)] transition-all disabled:opacity-70"
        >
          {busy ? <><Loader2 size={15} className="animate-spin" /> Signing in...</> : "Sign in"}
        </button>
        <p className="mt-5 text-center text-xs text-slate-500">
          Protected area · JWT secured
        </p>
      </motion.form>
    </div>
  );
};

export default Login;
